import type { TRIPOTWWasteQuantityDataType } from '../../types/form-r.ts';
import type { PotwID, QuestionnaireAnswers, QuestionnairePotw, WasteTransfer } from '../../types/questionnaires.ts';
import {
  formRBasisFromTransfers,
  type FormRTransferLocationRegistrar,
  type ReleaseByChemicalAndFormRLine,
  transfersToXmlCongeners,
} from './form-r-utils.ts';
import type { CasOrCategory } from '../../types/db.ts';
import { triTableIiiPotwTransferPercentagesMap } from '../../data/tri-table-iii-potw-transfer-percentages.ts';
import { metalsSet } from '../chemical-molecular-weight-caclulations.ts';
import { pfasSet } from '../../data/tri-pfas.ts';
import type { TransferComputed } from '../releases-calculate.ts';
import type { Warning } from '../../types/tri.ts';

export type FormRSection61PotwSection8Key = '8.1c' | '8.1d' | '8.7';

export function formRSection61PotwTransferPercentagesFor(
  casOrCategory: CasOrCategory,
  potwDistributionsKnown: QuestionnaireAnswers['potwDistributionsKnown'],
  warnings: Warning[],
): Record<FormRSection61PotwSection8Key, number> {
  const questionnairePercentages = potwDistributionsKnown[casOrCategory];
  if (questionnairePercentages) {
    let total = 0;
    for (const value of Object.values(questionnairePercentages)) {
      total += value;
    }
    if (total !== 100) {
      warnings.push({
        warningID: 'TW1131',
        type: 'questionnaire',
        subType: 'potwDistributionsKnown',
        message: `The sum of POTW distribution percentages for ${casOrCategory} must equal 100%.`,
        casOrCategory,
        blocksPriorYearSection8Calculation: true,
      });
    }
  }

  const percentages = questionnairePercentages ?? triTableIiiPotwTransferPercentagesMap.get(casOrCategory);
  if (percentages) {
    return {
      '8.1c': percentages['8.1c'],
      '8.1d': percentages['8.1d'],
      '8.7': percentages['8.7'],
    };
  }

  if (metalsSet.has(casOrCategory) || pfasSet.has(casOrCategory)) {
    return { '8.1c': 0, '8.1d': 100, '8.7': 0 };
  }

  return { '8.1c': 0, '8.1d': 0, '8.7': 100 };
}

export function formRSection61PotwReleases({
  chemicalName,
  casOrCategory,
  sectionSixOneReleases,
  wasteTransfers,
  potws,
  potwDistributionsKnown,
  registerTransferLocation,
  warnings,
}: {
  chemicalName: string;
  casOrCategory: CasOrCategory;
  sectionSixOneReleases: ReleaseByChemicalAndFormRLine | undefined;
  wasteTransfers: QuestionnaireAnswers['wasteTransfers'];
  potws: QuestionnaireAnswers['potws'];
  potwDistributionsKnown: QuestionnaireAnswers['potwDistributionsKnown'];
  registerTransferLocation: FormRTransferLocationRegistrar;
  warnings: Warning[];
}): TRIPOTWWasteQuantityDataType[] {
  const POTWWasteQuantity: TRIPOTWWasteQuantityDataType[] = [];
  if (sectionSixOneReleases) {
    let transferIndex = 0;
    const transfersByPotwID = new Map<
      PotwID,
      {
        totalPotwAmount: number;
        potw: QuestionnairePotw;
        transferInputs: WasteTransfer[];
        transferCalcs: TransferComputed[];
      }
    >();

    for (const transferCalc of sectionSixOneReleases.transfers) {
      let transferInput = wasteTransfers[transferCalc.transferID];
      if (!transferInput || transferInput.type !== 'potw') {
        warnings.push({
          warningID: 'TW1010',
          type: 'questionnaire',
          subType: transferCalc.transferID,
          casOrCategory,
          message: `${chemicalName} (${casOrCategory}), we were unable to find the associated transfer details from the questionnaire (${transferCalc.transferID}).`,
          blocksPriorYearSection8Calculation: true,
        });
        transferInput = {
          transferID: transferCalc.transferID,
          wasteStreamID: 'waste_stream_missing',
          allocationOrAnswer: '',
          type: 'potw',
          potwID: 'potw_missing',
        };
      }

      let potw = potws[transferInput.potwID as PotwID];
      if (!potw) {
        warnings.push({
          warningID: 'TW1011',
          type: 'questionnaire',
          subType: transferCalc.transferID,
          casOrCategory,
          message: `${chemicalName} (${casOrCategory}), we were unable to find the associated POTW details from the questionnaire (${transferInput.potwID}).`,
          blocksPriorYearSection8Calculation: true,
        });
        potw = {
          potwID: transferInput.potwID as PotwID,
          name: 'UNKNOWN POTW',
          address: 'UNKNOWN ADDRESS',
          city: 'UNKNOWN CITY',
          county: 'UNKNOWN ZIP',
          state: 'UNKNOWN STATE',
          zip: '00000',
        };
      }

      if (!transfersByPotwID.has(potw.potwID)) {
        transfersByPotwID.set(potw.potwID, {
          totalPotwAmount: 0,
          potw,
          transferInputs: [],
          transferCalcs: [],
        });
      }

      const accumulated = transfersByPotwID.get(potw.potwID)!;
      accumulated.totalPotwAmount += transferCalc.amountReleased;
      accumulated.transferInputs.push(transferInput);
      accumulated.transferCalcs.push(transferCalc);
    }

    for (const { potw, totalPotwAmount, transferCalcs } of transfersByPotwID.values()) {
      const formRTransferLocationIndex = registerTransferLocation(potw).TransferLocationSequenceNumber;

      const percentages = formRSection61PotwTransferPercentagesFor(casOrCategory, potwDistributionsKnown, warnings);

      for (const key of ['8.1c', '8.1d', '8.7'] as const) {
        const percentage = percentages[key];
        if (percentage === 0) {
          continue;
        }

        const fraction = percentage / 100;
        const WasteQuantityMeasure = totalPotwAmount * fraction;
        let POTWTransferTypeCode: string;
        if (key === '8.1c') {
          POTWTransferTypeCode = 'P33';
        } else if (key === '8.1d') {
          POTWTransferTypeCode = 'P36';
        } else {
          POTWTransferTypeCode = 'P39';
        }

        transferIndex += 1;
        POTWWasteQuantity.push({
          POTWSequenceNumber: transferIndex,
          POTWTransferSequenceNumber: formRTransferLocationIndex,
          POTWTransferTypeCode,
          WasteQuantity: {
            choice: 0,
            value: {
              WasteQuantityMeasure,
              WasteQuantityCatastrophicMeasure: null,
            },
          },
          QuantityBasisEstimation: {
            choice: 'QuantityBasisEstimationCode',
            value: formRBasisFromTransfers(transferCalcs, {
              chemicalName,
              casOrCategory,
              formRLine: '6.1',
              warnings,
            }),
          },

          // As far as I can tell, these are unused by TRI-MEweb (schema changelog notes "Schema component added to
          // enable TPL use of TRI schema." - but I have no idea what TPL is):
          QuantityDisposedLandfillPercentValue: null,
          QuantityDisposedOtherPercentValue: null,
          QuantityTreatedPercentValue: null,

          ToxicEquivalencyIdentification: transfersToXmlCongeners(
            casOrCategory,
            '6.1',
            transferCalcs,
            warnings,
            fraction,
          ),
          NonProductionWasteQuantityMeasure: null, // TODO: 6. Form R - May need to add support for Section 8.8 at some point.
        });
      }
    }
  } else {
    POTWWasteQuantity.push({
      WasteQuantity: {
        choice: 'NA',
        value: true,
      },

      // Omit all other XML elements:
      POTWSequenceNumber: null,
      POTWTransferSequenceNumber: null,
      QuantityBasisEstimation: {
        choice: 'NA',
        value: null,
      },
      POTWTransferTypeCode: null,
      QuantityDisposedLandfillPercentValue: null,
      QuantityDisposedOtherPercentValue: null,
      QuantityTreatedPercentValue: null,
      ToxicEquivalencyIdentification: null,
      NonProductionWasteQuantityMeasure: null,
    });
  }

  return POTWWasteQuantity;
}
